// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    AccExecClientResponse,
    AccountExecRequest,
    AddTargetRevenue,
    CeSystemUserResponse,
    CESystemUserList,
    CESystemUserRequest,
    CopyFiscalSaveRequest,
    HomeIndustriesList,
    HomeIndustriesRequest,
    MapChildParentRequest,
    OrganizationSizeList,
    SalesChangeRequest,
    SaveOrgSizeRequest,
    SaveSectorRequest,
    SectorList,
    SessionLogRequest,
    SessionLogResponse,
    TargetRevenueRequest,
    TargetRevenueResponse,
    ValidateDateOverlapRequest,
    addressList,
} from "./types/clientTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedCustomerHierarchyApi = createApi({
    reducerPath: "cclTokenedCustomerHierarchyApi",
    tagTypes: [""],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        searchTargetRevenue: builder.mutation<TargetRevenueResponse[], TargetRevenueRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/TargetRevenue/Search",
                method: "POST",
                body: data,
            }),
        }),
        getCeSystemUsers: builder.query<CESystemUserList[], void>({
            query: () => `customer-hierarchy-tool-api/v1.0/TargetRevenue/GetSystemUsers`,
        }),
        addUpdateTargetRevenue: builder.mutation<boolean, AddTargetRevenue>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/TargetRevenue/Add",
                method: "POST",
                body: data,
            }),
        }),
        deleteTargetRevenue: builder.mutation<boolean, number>({
            query: (targetRevenueID) => ({
                url: `customer-hierarchy-tool-api/v1.0/TargetRevenue/Delete/${targetRevenueID}`,
                method: "DELETE",
                body: targetRevenueID,
            }),
        }),
        searchHomeIndustrie: builder.mutation<HomeIndustriesList[], HomeIndustriesRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTIndustry/Search",
                method: "POST",
                body: data,
            }),
        }),
        MapChildParentkey: builder.mutation<boolean, MapChildParentRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTIndustry/MapChildparent",
                method: "POST",
                body: data,
            }),
        }),
        saveOrgSizeData: builder.mutation<boolean, SaveOrgSizeRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTIndustry/UpdateOrg",
                method: "POST",
                body: data,
            }),
        }),
        saveSectorData: builder.mutation<boolean, SaveSectorRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTIndustry/SectorUpdated",
                method: "POST",
                body: data,
            }),
        }),
        searchAccountExec: builder.mutation<AccExecClientResponse[], AccountExecRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTAccountExecutive/AccountSearch",
                method: "POST",
                body: data,
            }),
        }),
        searchCESystemUsers: builder.mutation<CeSystemUserResponse[], CESystemUserRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTAccountExecutive/CEUserSearch",
                method: "POST",
                body: data,
            }),
        }),
        getSectorList: builder.query<SectorList[], void>({
            query: () => `customer-hierarchy-tool-api/v1.0/CHTIndustry/GetSectorList`,
        }),
        getOrgSizeList: builder.query<OrganizationSizeList[], void>({
            query: () => `customer-hierarchy-tool-api/v1.0/CHTIndustry/GetOrgSizeList`,
        }),
        getAddressList: builder.mutation<addressList[], number>({
            query: (data) => ({
                url: `customer-hierarchy-tool-api/v1.0/CHTIndustry/GetAddressList/${data}`,
                method: "POST",
                body: data,
            }),
        }),
        SaveFiscalChanges: builder.mutation<boolean, CopyFiscalSaveRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTAccountExecutive/SaveFiscalData",
                method: "POST",
                body: data,
            }),
        }),
        DeleteAccountExecutiveMapperID: builder.mutation<boolean, number>({
            query: (data) => ({
                url: `customer-hierarchy-tool-api/v1.0/CHTAccountExecutive/DeleteAccountExecMapperID/${data}`,
                method: "DELETE",
                body: data,
            }),
        }),
        searchSessionLog: builder.mutation<SessionLogResponse[], SessionLogRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/SessionLog/Search",
                method: "POST",
                body: data,
            }),
        }),
        saveSalesChange: builder.mutation<boolean, SalesChangeRequest>({
            query: (data) => ({
                url: "customer-hierarchy-tool-api/v1.0/CHTAccountExecutive/SaveSalesChange",
                method: "POST",
                body: data,
            }),
        }),
        validateDateOverlap: builder.mutation<number, ValidateDateOverlapRequest>({
            query: (data) => ({
                url: `customer-hierarchy-tool-api/v1.0/CHTAccountExecutive/ValidateDateOverlap?parentKey=${data.parentKey}&esDate=${encodeURIComponent(new Date(data.esDate).toISOString())}&eeDate=${encodeURIComponent(new Date(data.eeDate).toISOString())}&fiscal=${data.fiscal}`,
                method: "POST",
            }),
        }),
        getDistinctActions: builder.query<string[], void>({
            query: () => `customer-hierarchy-tool-api/v1.0/SessionLog/GetDistinctActions`,
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useSearchTargetRevenueMutation,
    useGetCeSystemUsersQuery,
    useAddUpdateTargetRevenueMutation,
    useDeleteTargetRevenueMutation,
    useSearchHomeIndustrieMutation,
    useSearchAccountExecMutation,
    useSearchCESystemUsersMutation,
    useMapChildParentkeyMutation,
    useSaveOrgSizeDataMutation,
    useSaveSectorDataMutation,
    useGetOrgSizeListQuery,
    useGetSectorListQuery,
    useGetAddressListMutation,
    useSaveFiscalChangesMutation,
    useDeleteAccountExecutiveMapperIDMutation,
    useSearchSessionLogMutation,
    useSaveSalesChangeMutation,
    useValidateDateOverlapMutation,
    useGetDistinctActionsQuery,
} = cclTokenedCustomerHierarchyApi;
